import type {
  AuthGoogleSignInOutcome,
  AuthGoogleSignInRequest,
  AuthInvitePreview,
  AuthProviders,
  AuthSessionTokens,
  OperatorLocale,
} from "@backchannel/tax-contracts";
import type { AppEnvConfig } from "~/config/env";
import { logDiagnostic } from "~/lib/redact";
import { AuthError } from "./errors";
import {
  PasskeyCancelledError,
  PasskeyCeremonyError,
  PasskeyOriginError,
  type PasskeyRequestOptionsJSON,
  PasskeyTimeoutError,
  PasskeyUnsupportedError,
  getPasskeyAssertion,
} from "./passkeys";
import type { TokenSet } from "./token-store";

export const DEFAULT_AUTH_REQUEST_TIMEOUT_MS = 15_000;
export const DEFAULT_PASSKEY_REQUEST_TIMEOUT_MS = 15_000;

async function fetchWithTimeout(
  fetchImpl: typeof fetch,
  input: RequestInfo | URL,
  init: RequestInit,
  timeoutMs: number,
  onTimeout: () => Error,
): Promise<Response> {
  const controller = new AbortController();
  let timedOut = false;
  let timeoutFailure: Error | null = null;
  let rejectTimeout!: (reason: Error) => void;
  const timeoutPromise = new Promise<never>((_resolve, reject) => {
    rejectTimeout = reject;
  });
  const timeoutId = globalThis.setTimeout(() => {
    timedOut = true;
    controller.abort();
    timeoutFailure = onTimeout();
    rejectTimeout(timeoutFailure);
  }, timeoutMs);

  try {
    return await Promise.race([
      fetchImpl(input, { ...init, signal: controller.signal }),
      timeoutPromise,
    ]);
  } catch (error) {
    if (timedOut) throw timeoutFailure ?? onTimeout();
    throw error;
  } finally {
    globalThis.clearTimeout(timeoutId);
  }
}

function toTokenSet(
  tokens: AuthSessionTokens,
  now = Date.now(),
  source: TokenSet["source"] = "platform",
): TokenSet {
  return {
    idToken: tokens.idToken,
    refreshToken: tokens.refreshToken,
    expiresAt: now + tokens.expiresIn * 1000,
    source,
  };
}

function asSession(data: unknown, source: TokenSet["source"] = "platform"): TokenSet {
  if (!data || typeof data !== "object") {
    throw new AuthError("exchange", "The identity service returned an invalid session");
  }
  const session = data as Partial<AuthSessionTokens>;
  if (typeof session.idToken !== "string" || typeof session.refreshToken !== "string") {
    throw new AuthError("exchange", "The identity service returned an incomplete session");
  }
  const expiresIn = typeof session.expiresIn === "number" ? session.expiresIn : 900;
  return toTokenSet(
    {
      tokenType: "Bearer",
      idToken: session.idToken,
      refreshToken: session.refreshToken,
      expiresIn,
    },
    Date.now(),
    source,
  );
}

export type PlatformAuthClient = ReturnType<typeof createPlatformAuthClient>;

export function createPlatformAuthClient(config: AppEnvConfig, fetchImpl: typeof fetch = fetch) {
  function endpoint(path: string): string {
    const base = config.apiBaseUrl.trim().replace(/\/+$/, "");
    if (!base) {
      throw new AuthError("config", "The Tax API origin is not configured");
    }
    return `${base}${path}`;
  }

  async function post(
    path: string,
    body: unknown,
    timeoutMs = DEFAULT_AUTH_REQUEST_TIMEOUT_MS,
  ): Promise<Response> {
    try {
      return await fetchWithTimeout(
        fetchImpl,
        endpoint(path),
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(body),
          credentials: "omit",
          mode: "cors",
        },
        timeoutMs,
        () =>
          new AuthError("network", "The identity service did not respond in time", null, "timeout"),
      );
    } catch (error) {
      if (error instanceof AuthError) throw error;
      if (error instanceof TypeError) {
        throw new AuthError(
          "network",
          "The identity service could not be reached",
          null,
          "unavailable",
        );
      }
      throw error;
    }
  }

  async function readJson(response: Response): Promise<unknown> {
    return response.json().catch(() => ({}));
  }

  return {
    async passwordSignIn(email: string, password: string): Promise<TokenSet> {
      const normalizedEmail = email.trim().toLowerCase();
      if (!normalizedEmail || !password) {
        throw new AuthError("credentials", "Email and password are required");
      }
      const response = await post("/v1/auth/login", { email: normalizedEmail, password });
      if (!response.ok) {
        throw new AuthError("credentials", "Sign-in failed", null, String(response.status));
      }
      return asSession(await readJson(response));
    },

    async refresh(
      refreshToken: string,
      source: TokenSet["source"] = "platform",
    ): Promise<TokenSet> {
      const response = await post("/v1/auth/refresh", { refreshToken });
      if (!response.ok) {
        throw new AuthError("exchange", "Session refresh failed", null, String(response.status));
      }
      return asSession(await readJson(response), source);
    },

    async refreshPasskey(refreshToken: string, _idToken: string): Promise<TokenSet> {
      const response = await post("/v1/auth/refresh", { refreshToken });
      if (!response.ok) {
        throw new AuthError("passkey", "Session refresh failed", null, String(response.status));
      }
      return asSession(await readJson(response), "passkey");
    },

    async requestPasswordReset(email: string, locale?: OperatorLocale): Promise<void> {
      const normalizedEmail = email.trim().toLowerCase();
      if (!normalizedEmail) {
        throw new AuthError("passwordReset", "Email is required", null, "missingEmail");
      }
      const response = await post("/v1/auth/password/forgot", { email: normalizedEmail, locale });
      if (!response.ok) {
        throw new AuthError(
          "passwordReset",
          "Password reset could not be started",
          null,
          String(response.status),
        );
      }
    },

    async resetPassword(token: string, password: string): Promise<void> {
      if (!token.trim() || !password) {
        throw new AuthError(
          "passwordReset",
          "Password recovery details are required",
          null,
          "invalidInput",
        );
      }
      const response = await post("/v1/auth/password/reset", { token, password });
      if (!response.ok) {
        throw new AuthError(
          "passwordReset",
          "Password reset failed",
          null,
          String(response.status),
        );
      }
    },

    async previewInvite(token: string): Promise<AuthInvitePreview> {
      const response = await fetchWithTimeout(
        fetchImpl,
        endpoint(`/v1/auth/invites/${encodeURIComponent(token)}`),
        { method: "GET", credentials: "omit", mode: "cors" },
        DEFAULT_AUTH_REQUEST_TIMEOUT_MS,
        () => new AuthError("network", "The invite could not be loaded", null, "timeout"),
      );
      if (response.status === 404 || response.status === 401) {
        throw new AuthError("credentials", "The invite is invalid or has expired", null, "expired");
      }
      if (!response.ok) {
        throw new AuthError(
          "network",
          "The invite could not be loaded",
          null,
          String(response.status),
        );
      }
      return (await readJson(response)) as AuthInvitePreview;
    },

    async acceptInvite(token: string, password: string, displayName?: string): Promise<TokenSet> {
      const response = await post(`/v1/auth/invites/${encodeURIComponent(token)}/accept`, {
        password,
        ...(displayName ? { displayName } : {}),
      });
      if (!response.ok) {
        throw new AuthError(
          "credentials",
          "The invite could not be accepted",
          null,
          String(response.status),
        );
      }
      return asSession(await readJson(response));
    },

    /**
     * Optional providers enabled by the deployed API. An API without the
     * endpoint (404) or with Google switched off answers `google: null`; a
     * failed lookup (5xx, timeout, network) rejects so the sign-in card can
     * tell "not offered" from "could not check" and offer a retry.
     */
    async providers(): Promise<AuthProviders> {
      try {
        const response = await fetchWithTimeout(
          fetchImpl,
          endpoint("/v1/auth/providers"),
          { method: "GET", credentials: "omit", mode: "cors" },
          DEFAULT_AUTH_REQUEST_TIMEOUT_MS,
          () => new AuthError("network", "Sign-in providers did not respond in time"),
        );
        if (response.status >= 500) {
          throw new AuthError("network", `Sign-in providers answered ${response.status}`);
        }
        if (!response.ok) return { google: null };
        const data = (await readJson(response)) as Partial<AuthProviders>;
        const google = data.google;
        if (
          !(google && typeof google.clientId === "string" && typeof google.redirectUri === "string")
        ) {
          return { google: null };
        }
        const redirectUris = Array.isArray(google.redirectUris)
          ? google.redirectUris.filter((uri): uri is string => typeof uri === "string")
          : [];
        return {
          google: {
            clientId: google.clientId,
            redirectUri: google.redirectUri,
            redirectUris: redirectUris.length ? redirectUris : [google.redirectUri],
            ...(typeof google.hostedDomain === "string" && google.hostedDomain
              ? { hostedDomain: google.hostedDomain }
              : {}),
          },
        };
      } catch (error) {
        logDiagnostic("auth", { step: "providers", error });
        throw error instanceof AuthError
          ? error
          : new AuthError("network", "Sign-in providers could not be checked");
      }
    },

    async googleSignIn(
      request: AuthGoogleSignInRequest,
    ): Promise<{ tokens: TokenSet; outcome: AuthGoogleSignInOutcome }> {
      const response = await post("/v1/auth/google", request);
      if (!response.ok) {
        const code =
          response.status === 403
            ? "notInvited"
            : response.status === 401
              ? "rejected"
              : response.status === 503
                ? "unavailable"
                : "error";
        throw new AuthError("google", "Google sign-in failed", null, code);
      }
      const data = await readJson(response);
      const outcome = (data as { outcome?: unknown } | null)?.outcome;
      return {
        tokens: asSession(data),
        outcome: outcome === "PROVISIONED" || outcome === "INVITE_ACCEPTED" ? outcome : "EXISTING",
      };
    },

    async passkeySignIn(email = ""): Promise<TokenSet> {
      try {
        const normalizedEmail = email.trim().toLowerCase();
        const optionsResponse = await post(
          "/v1/auth/passkeys/options",
          normalizedEmail ? { email: normalizedEmail } : {},
          DEFAULT_PASSKEY_REQUEST_TIMEOUT_MS,
        );
        if (!optionsResponse.ok) {
          throw new AuthError(
            "passkey",
            "Passkey service could not complete sign-in",
            null,
            "error",
          );
        }
        const options = (await readJson(optionsResponse)) as PasskeyRequestOptionsJSON;
        const credential = await getPasskeyAssertion(options);
        const verifyResponse = await post(
          "/v1/auth/passkeys/verify",
          {
            ...(normalizedEmail ? { email: normalizedEmail } : {}),
            ...credential,
          },
          DEFAULT_PASSKEY_REQUEST_TIMEOUT_MS,
        );
        if (!verifyResponse.ok) {
          throw new AuthError(
            "passkey",
            "Passkey service could not complete sign-in",
            null,
            "error",
          );
        }
        return asSession(await readJson(verifyResponse), "passkey");
      } catch (error) {
        if (error instanceof AuthError) throw error;
        if (error instanceof PasskeyUnsupportedError) {
          throw new AuthError("passkey", error.message, null, "unsupported");
        }
        if (error instanceof PasskeyCancelledError) {
          throw new AuthError("passkey", error.message, null, "cancelled");
        }
        if (error instanceof PasskeyTimeoutError) {
          throw new AuthError("passkey", error.message, null, "timeout");
        }
        if (error instanceof PasskeyOriginError) {
          throw new AuthError("passkey", error.message, null, "origin");
        }
        if (error instanceof PasskeyCeremonyError) {
          throw new AuthError("passkey", error.message, null, "error");
        }
        logDiagnostic("auth", { step: "passkey", error });
        throw new AuthError("passkey", "Passkey verification failed", null, "error");
      }
    },

    /**
     * WebAuthn conditional mediation: offers the operator's saved passkeys
     * in the email field's own autofill dropdown. Always usernameless
     * (discoverable) — the server issues an empty `allowCredentials` list so
     * the authenticator itself resolves the account. `signal` is owned by
     * the caller, which aborts it when the operator picks another method,
     * the field leaves the screen, or the challenge needs a refresh.
     */
    async passkeyAutofill(signal: AbortSignal): Promise<TokenSet> {
      try {
        const optionsResponse = await post(
          "/v1/auth/passkeys/options",
          {},
          DEFAULT_PASSKEY_REQUEST_TIMEOUT_MS,
        );
        if (!optionsResponse.ok) {
          throw new AuthError(
            "passkey",
            "Passkey service could not complete sign-in",
            null,
            "error",
          );
        }
        const options = (await readJson(optionsResponse)) as PasskeyRequestOptionsJSON;
        const credential = await getPasskeyAssertion(options, { signal, mediation: "conditional" });
        const verifyResponse = await post(
          "/v1/auth/passkeys/verify",
          credential,
          DEFAULT_PASSKEY_REQUEST_TIMEOUT_MS,
        );
        if (!verifyResponse.ok) {
          throw new AuthError(
            "passkey",
            "Passkey service could not complete sign-in",
            null,
            "error",
          );
        }
        return asSession(await readJson(verifyResponse), "passkey");
      } catch (error) {
        if (error instanceof AuthError) throw error;
        if (error instanceof PasskeyUnsupportedError) {
          throw new AuthError("passkey", error.message, null, "unsupported");
        }
        if (error instanceof PasskeyCancelledError) {
          throw new AuthError("passkey", error.message, null, "cancelled");
        }
        if (error instanceof PasskeyOriginError) {
          throw new AuthError("passkey", error.message, null, "origin");
        }
        if (error instanceof PasskeyCeremonyError) {
          throw new AuthError("passkey", error.message, null, "error");
        }
        logDiagnostic("auth", { step: "passkey-autofill", error });
        throw new AuthError("passkey", "Passkey verification failed", null, "error");
      }
    },
  };
}
